<script setup>
/**
 * UserProfileOffcanvas
 * Dynamic sliding user profile configuration panel.
 * Registers an on-the-fly user management schema via the central CMS forms engine,
 * capturing save callbacks natively to update active administrative contexts.
 */
import {ref, computed, onMounted, onBeforeUnmount} from 'vue'
import {useFormsStore} from '@/stores/forms'
import {useUserStore} from '@/stores/user'

// Core CMS Architecture Components
import Form from '@/common/form/Form.vue'
import FormSaveBtn from '@/common/form/FormSaveBtn.vue'
import {Offcanvas} from "bootstrap"

// Pinia Stores
const formsStore = useFormsStore()
const userStore = useUserStore()

// --- Functional State Layers ---

const formId = ref('')
const offcanvasElementRef = ref(null)
const bsOffcanvas = ref(null)

const tableName = 'users'

// --- Synchronous Computeds ---

/**
 * Accesses global session profiles to pull credentials for the active operator.
 */
const currentUserId = computed(() => {
    return userStore.user?.id || 0
})

// --- Core Forms Engine Orchestration ---

/**
 * Generates and registers an isolated runtime schema template
 * inside the global forms state manager module.
 */
const createProfileForm = () => {
    if (!currentUserId.value) return

    // Standard CMS key composition matching: "form:tableName:id"
    formId.value = `form:${tableName}:${currentUserId.value}`

    formsStore.createForm({
        tableName: tableName,
        id: currentUserId.value,
        formSmall: true,
        helpText: false,
        fieldNames: [
            'firstName',
            'lastName',
            'email',
            'phoneNumber',
            'username',
            'password'
        ],
        currentData: {}, // Hydrates natively from database record mapping inside the store action
        onSave: (savedUserObj) => {
            // Optional: Dispatch a system action to update the visible header avatar or name strings instantly
            // userStore.updateActiveUserContext(savedUserObj);

            // Programmatically retracts the offcanvas frame panel upon a successful API write sequence
            dismissOffcanvasPanel()
        }
    })
}

/**
 * Safely unbinds the form instance state from the global tracking stack when collapsed.
 */
const destroyProfileForm = () => {
    formId.value = ''
}

// --- Offcanvas Interactive Closures ---

/**
 * Programmatically finds the active slide wrapper instance and triggers a hide collapse animation loop.
 */
const dismissOffcanvasPanel = () => {
    if (offcanvasElementRef.value) {
        bsOffcanvas.value?.hide()
    }
}

// --- Lifecycle Event Wire-ups ---

onMounted(() => {
    bsOffcanvas.value = new Offcanvas(offcanvasElementRef.value)

    // Binds forms generation rules cleanly against native Bootstrap 5 initialization timelines
    offcanvasElementRef.value.addEventListener('show.bs.offcanvas', createProfileForm)
    offcanvasElementRef.value.addEventListener('hidden.bs.offcanvas', destroyProfileForm)
})

onBeforeUnmount(() => {
    // Structural garbage collection cleanup to shield against SPA performance memory leaks
    offcanvasElementRef.value.removeEventListener('show.bs.offcanvas', createProfileForm)
    offcanvasElementRef.value.removeEventListener('hidden.bs.offcanvas', destroyProfileForm)
})
</script>

<template>
    <div
        id="userProfileOffcanvas"
        class="offcanvas offcanvas-end"
        tabindex="-1"
        aria-labelledby="userProfileOffcanvasLabel"
        ref="offcanvasElementRef"
    >
        <div class="offcanvas-header border-bottom">
            <h5 id="userProfileOffcanvasLabel" class="offcanvas-title fw-bold text-body">
                User Profile
            </h5>
            <button
                type="button"
                class="btn-close"
                data-bs-dismiss="offcanvas"
                aria-label="Close"
            />
        </div>

        <div class="offcanvas-body d-flex flex-column justify-content-between">

            <div class="flex-fill">
                <Form v-if="formId" :form-id="formId" :hide-help-text="true"/>

                <div v-else class="py-5 text-center text-muted">
                    <div class="spinner-border text-secondary mb-2" role="status"/>
                </div>
            </div>

            <footer v-if="formId" class="text-center pt-3 border-top mt-auto bg-body">
                <FormSaveBtn
                    :button-text="'Save Changes'"
                    :cancel-on-save="true"
                    :form-id="formId"
                    class="w-100 py-2 btn btn-primary"
                    :form="formId"
                />
            </footer>

        </div>
    </div>
</template>